import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";

// Default type used when an org-meta notification has an unknown/missing "type".
export const DEFAULT_NOTIFICATION_TYPE = "info";

// Visual config per notification type. Colors are chosen to read as soft-filled
// banners (light background + subtle border) matching the app's alert styling.
export const NOTIFICATION_TYPE_CONFIG = {
  error: {
    icon: ErrorOutlineIcon,
    background: "#FDECEA",
    border: "#F5C6C3",
    iconColor: "#D32F2F",
    textColor: "#611A15",
  },
  warning: {
    icon: WarningAmberIcon,
    background: "#FFF8E6",
    border: "#FFE3A3",
    iconColor: "#ED9B00",
    textColor: "#663C00",
  },
  notice: {
    icon: CampaignOutlinedIcon,
    background: "#F3E8FD",
    border: "#E0C6F5",
    iconColor: "#7B1FA2",
    textColor: "#4A148C",
  },
  info: {
    icon: InfoOutlinedIcon,
    background: "#E8F4FD",
    border: "#BEE0FB",
    iconColor: "#0288D1",
    textColor: "#0D3C61",
  },
  success: {
    icon: CheckCircleOutlineIcon,
    background: "#EDF7ED",
    border: "#C3E6C4",
    iconColor: "#2E7D32",
    textColor: "#1E4620",
  },
  maintenance: {
    icon: BuildOutlinedIcon,
    background: "#ECEFF1",
    border: "#CFD8DC",
    iconColor: "#546E7A",
    textColor: "#37474F",
  },
};

// Resolve a (possibly unknown) type string to a known config entry.
export const getTypeConfig = (type) =>
  NOTIFICATION_TYPE_CONFIG[type] ||
  NOTIFICATION_TYPE_CONFIG[DEFAULT_NOTIFICATION_TYPE];

/**
 * Pure filter for org-meta notifications. Kept free of React/Date so it can be
 * unit tested directly (see CLAUDE.md testing convention).
 *
 * @param {Array} notifications  raw array from org meta
 * @param {Object} options
 * @param {number} options.now          current time in ms (e.g. dayjs().valueOf())
 * @param {string[]|Set} options.dismissedIds  ids the user has dismissed
 * @returns {Array} notifications that should currently render
 */
export const getVisibleNotifications = (notifications, { now = 0, dismissedIds = [] } = {}) => {
  if (!Array.isArray(notifications)) return [];

  const dismissed = dismissedIds instanceof Set ? dismissedIds : new Set(dismissedIds);

  return notifications.filter((n) => {
    if (!n || typeof n !== "object") return false;
    // Needs something to show.
    if (!n.message && !n.title) return false;
    // Explicit kill-switch (defaults to enabled).
    if (n.enabled === false) return false;
    // Dismissed (only dismissible ones with a stable id can be dismissed).
    if (n.id && dismissed.has(n.id)) return false;
    // Display window.
    if (n.startAt) {
      const start = new Date(n.startAt).getTime();
      if (!Number.isNaN(start) && now < start) return false;
    }
    if (n.endAt) {
      const end = new Date(n.endAt).getTime();
      if (!Number.isNaN(end) && now > end) return false;
    }
    return true;
  });
};

// localStorage key holding an array of dismissed notification ids.
export const DISMISSED_NOTIFICATIONS_CACHE_KEY = "dismissedOrgNotifications";
